import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, animate } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChartLine, Shield, GraduationCap, Download, DeviceMobile, Eye,
  CaretRight, Sparkle, Trophy, Robot, Users, Lock, Lightning, Star,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import DiveDeeperStack from '../components/DiveDeeperStack';

/* -------------------------------------------------------------------------- */
/*                                Sub-components                              */
/* -------------------------------------------------------------------------- */

// Animated counter — counts from 0 to target when in view
const StatCounter = ({ value, prefix = '', suffix = '', duration = 1.6, format }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, { duration, ease: [0.22, 0.7, 0.18, 1] });
    const unsub = mv.on('change', (v) => {
      setDisplay(format ? format(v) : Math.round(v).toLocaleString());
    });
    return () => { controls.stop(); unsub(); };
  }, [inView, value, duration, mv, format]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
};

// Laptop frame that hosts the "Dive Deeper" swipe stack on its screen
const LaptopMockup = ({ onCta }) => (
  <div className="relative w-full" data-testid="hero-laptop-mockup">
    <div className="relative rounded-t-2xl border-[3px] border-zinc-700/80 bg-zinc-950 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.25)] overflow-hidden">
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900/80 border-b border-white/5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
        <div className="ml-3 flex-1 max-w-[240px] rounded-md bg-zinc-800/80 px-2 py-1 text-[10px] text-zinc-500 truncate">
          teenvest4growth.com/dashboard
        </div>
      </div>

      {/* Dive Deeper — interactive nested-frame preview */}
      <div className="p-2.5 sm:p-5 bg-[radial-gradient(circle_at_30%_0%,rgba(212,175,55,0.09),transparent_60%)]">
        <DiveDeeperStack onCta={onCta} />
      </div>
    </div>

    {/* Laptop base */}
    <div className="mx-auto h-[10px] bg-gradient-to-b from-zinc-700 to-zinc-900 rounded-b-xl w-[104%] -ml-[2%]" />
    <div className="mx-auto h-[3px] bg-black rounded-b w-[30%]" />
  </div>
);

/* -------------------------------------------------------------------------- */
/*                                Main Landing                                */
/* -------------------------------------------------------------------------- */

const Landing = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user, startGuestSession } = useAuth();

  const handleGuestPreview = async () => {
    setGuestLoading(true);
    try {
      await startGuestSession();
      toast.success('Welcome! Exploring as guest', { duration: 3000 });
      navigate('/dashboard');
    } catch (e) {
      toast.error('Could not start guest session. Try again.');
    } finally {
      setGuestLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setShowInstallButton(true); };
    window.addEventListener('beforeinstallprompt', handler);
    if (window.matchMedia('(display-mode: standalone)').matches) setShowInstallButton(false);

    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert('To install TeenVest:\n\niPhone/iPad: Tap Share → Add to Home Screen\n\nAndroid: Tap Menu (⋮) → Install App\n\nDesktop: Look for install icon in address bar');
      return;
    }
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const openRegister = () => { setAuthMode('register'); setShowAuth(true); };
  const openLogin    = () => { setAuthMode('login');    setShowAuth(true); };

  if (showAuth) {
    if (authMode === 'login')  return <Login    onSwitchToRegister={() => setAuthMode('register')} onForgotPassword={() => setAuthMode('forgot')} onBack={() => setShowAuth(false)} />;
    if (authMode === 'forgot') return <ForgotPassword onBack={() => setAuthMode('login')} />;
    return <Register onSwitchToLogin={() => setAuthMode('login')} onBack={() => setShowAuth(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Ambient glow — clamped on mobile to avoid overflow */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute top-[-20%] left-[10%] w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gold/10 rounded-full blur-[120px]" />
        <div className="absolute top-[30%] right-[-10%] w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-yellow-600/8 rounded-full blur-[120px]" />
      </div>

      {/* ============================ STICKY NAV ============================ */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled
            ? 'bg-black/70 backdrop-blur-xl border-b border-white/5'
            : 'bg-black/40 backdrop-blur-md border-b border-transparent'
        }`}
        data-testid="landing-sticky-nav"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/about" className="flex items-center gap-2 group" data-testid="landing-nav-logo">
            <img src="/logo.png" alt="TeenVest" className="w-8 h-8 group-hover:scale-110 transition-transform" />
            <span className="font-unbounded font-bold text-lg bg-gradient-to-r from-yellow-300 to-yellow-600 bg-clip-text text-transparent">
              TeenVest
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-300">
            <Link to="/about" className="hover:text-gold transition-colors" data-testid="landing-nav-about">About</Link>
            <Link to="/blog" className="hover:text-gold transition-colors" data-testid="landing-nav-blog">Blog</Link>
            <Link to="/central-message" className="hover:text-gold transition-colors" data-testid="landing-nav-central">Manifesto</Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={openLogin}
              className="hidden md:inline-flex text-sm text-zinc-300 hover:text-white px-3 py-1.5 rounded-full transition-colors"
              data-testid="landing-nav-login"
            >
              Log in
            </button>
            <button
              onClick={openRegister}
              className="text-sm font-bold text-black bg-gradient-to-r from-yellow-300 to-yellow-500 px-4 py-1.5 rounded-full hover:scale-[1.03] transition-transform"
              data-testid="landing-nav-signup"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ================================ HERO ================================ */}
      <section className="relative z-10 pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 text-left"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/20 text-gold text-xs font-medium mb-6">
              <Sparkle size={12} weight="fill" /> Now with AI Stock Analyst
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-unbounded font-black leading-[1.05] tracking-tight mb-6">
              The stock market,
              <br />
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                learned safely.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-8 max-w-xl">
              $25,000 in virtual money. 1,000+ real stocks. Six game modes.
              Built for teens who want to understand investing before they ever risk a dollar.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-8">
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-300 to-yellow-500 text-black font-bold text-base px-7 py-6 rounded-full hover:scale-[1.03] transition-transform shadow-[0_10px_40px_-8px_rgba(212,175,55,0.5)]"
                onClick={openRegister}
                data-testid="get-started-btn"
              >
                Get Started Free <CaretRight size={16} weight="bold" className="ml-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border border-white/15 bg-white/5 text-white font-semibold text-base px-6 py-6 rounded-full hover:bg-white/10 transition-colors"
                onClick={handleGuestPreview}
                disabled={guestLoading}
                data-testid="guest-preview-btn"
              >
                <Eye size={16} weight="fill" className="mr-2 text-gold" />
                {guestLoading ? 'Loading...' : 'Try Preview'}
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><Lock size={12} weight="fill" className="text-emerald-400" /> 0 real money</span>
              <span className="flex items-center gap-1.5"><Users size={12} weight="fill" className="text-cyan-400" /> Ages 13+</span>
              <span className="flex items-center gap-1.5"><Star size={12} weight="fill" className="text-gold" /> 150+ students</span>
              <button
                onClick={openLogin}
                className="text-zinc-400 hover:text-gold transition-colors underline underline-offset-2"
                data-testid="login-btn"
              >
                Log in
              </button>
            </div>
          </motion.div>

          {/* Right: interactive preview */}
          <div className="lg:col-span-7 relative -mx-6 sm:mx-0">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Laptop frame hosting the Dive Deeper stack */}
              <div className="relative">
                <LaptopMockup onCta={openRegister} />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Install button — kept, moved to a subtle secondary spot */}
        {!user && showInstallButton && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="max-w-7xl mx-auto px-6 mt-20 text-left"
            data-testid="landing-install-button-wrapper"
          >
            <button
              onClick={handleInstallClick}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gold text-xs font-medium hover:bg-white/10 transition-colors"
            >
              <DeviceMobile size={14} weight="duotone" />
              Install as an app · iOS · Android · Desktop
              <Download size={12} />
            </button>
          </motion.div>
        )}
      </section>

      {/* ============================ STATS TICKER ============================ */}
      <section className="relative z-10 border-y border-white/5 bg-black/40 backdrop-blur-sm py-14" data-testid="landing-stats-band">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { label: 'Students learning',     value: 150, suffix: '+' },
            { label: 'Trades executed',       value: 6420, suffix: '' },
            { label: 'XP earned this month',  value: 84200, suffix: '' },
            { label: 'Avg. 30-day return',    value: 11.3,  suffix: '%', format: (v) => v.toFixed(1) },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="text-left"
            >
              <p className="font-unbounded font-black text-4xl md:text-5xl bg-gradient-to-r from-yellow-200 to-yellow-500 bg-clip-text text-transparent">
                <StatCounter value={s.value} suffix={s.suffix} format={s.format} />
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============================ FEATURES BENTO ============================ */}
      <section className="relative z-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Why TeenVest</p>
            <h2 className="text-4xl md:text-5xl font-unbounded font-bold leading-tight mb-4">
              Every feature exists to teach.
            </h2>
            <p className="text-lg text-zinc-400">Not to make you trade. Not to sell you a subscription. To help you understand markets before they matter.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Big feature: Real markets */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5 }}
              className="md:col-span-2 md:row-span-2 rounded-3xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 p-8 hover:border-gold/30 transition-colors"
              data-testid="feature-real-markets"
            >
              <ChartLine size={28} weight="duotone" className="text-gold mb-6" />
              <h3 className="text-2xl md:text-3xl font-unbounded font-bold text-white mb-3">Real markets. Zero risk.</h3>
              <p className="text-zinc-400 leading-relaxed max-w-lg">Every ticker on TeenVest is a real, live-quoted asset from Yahoo Finance — 1,000+ US stocks, ETFs, crypto, commodities and currencies. You trade with $25,000 in virtual money, so the lessons stick without the losses.</p>
              <div className="mt-8 flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['AAPL', 'NVDA', 'BTC'].map((s) => (
                    <div key={s} className="w-8 h-8 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center text-[9px] text-gold font-mono font-bold">{s.slice(0,3)}</div>
                  ))}
                </div>
                <span className="text-xs text-zinc-500">1,000+ live tickers</span>
              </div>
            </motion.div>

            {/* AI Analyst */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5, delay: 0.05 }}
              className="rounded-3xl bg-gradient-to-br from-purple-500/10 to-white/[0.01] border border-white/10 p-6 hover:border-purple-400/40 transition-colors"
              data-testid="feature-ai-analyst"
            >
              <Robot size={24} weight="duotone" className="text-purple-300 mb-4" />
              <h3 className="text-xl font-unbounded font-bold text-white mb-2">AI Stock Analyst</h3>
              <p className="text-sm text-zinc-400">Personalised risk profile + 3-5 buy ideas from a curated universe, powered by GPT-5.</p>
            </motion.div>

            {/* Game modes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-3xl bg-gradient-to-br from-cyan-500/10 to-white/[0.01] border border-white/10 p-6 hover:border-cyan-400/40 transition-colors"
              data-testid="feature-game-modes"
            >
              <Lightning size={24} weight="duotone" className="text-cyan-300 mb-4" />
              <h3 className="text-xl font-unbounded font-bold text-white mb-2">Six Game Modes</h3>
              <p className="text-sm text-zinc-400">Chaos, Oracle, Wolf, News Reactor, AI Bubble and Learn. Each teaches a different skill.</p>
            </motion.div>

            {/* Community */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5, delay: 0.15 }}
              className="rounded-3xl bg-gradient-to-br from-emerald-500/10 to-white/[0.01] border border-white/10 p-6 hover:border-emerald-400/40 transition-colors"
              data-testid="feature-community"
            >
              <Users size={24} weight="duotone" className="text-emerald-300 mb-4" />
              <h3 className="text-xl font-unbounded font-bold text-white mb-2">Community + Bets</h3>
              <p className="text-sm text-zinc-400">Share picks, join the feed, challenge friends to peer-to-peer bets — bragging rights only.</p>
            </motion.div>

            {/* Progression */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-3xl bg-gradient-to-br from-gold/10 to-white/[0.01] border border-white/10 p-6 hover:border-gold/40 transition-colors"
              data-testid="feature-progression"
            >
              <Trophy size={24} weight="duotone" className="text-gold mb-4" />
              <h3 className="text-xl font-unbounded font-bold text-white mb-2">XP · Ranks · Badges</h3>
              <p className="text-sm text-zinc-400">Level up from Beginner to Master. Unlock the Indian Stock Market at 500 XP.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section className="relative z-10 py-24 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">How it works</p>
            <h2 className="text-4xl md:text-5xl font-unbounded font-bold leading-tight">From sign-up to first trade in under 60 seconds.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: '01', title: 'Sign up', body: 'Email + password. No credit card. No age gate. Free forever.' },
              { n: '02', title: 'Get $25,000',  body: 'Virtual portfolio funded instantly. Buy any real ticker at live prices.' },
              { n: '03', title: 'Learn & level up', body: 'Complete lessons, win game modes, climb the leaderboard. 200 XP per mode.' },
            ].map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className="rounded-3xl bg-black/40 border border-white/10 p-8 hover:border-gold/30 transition-colors"
              >
                <p className="font-unbounded font-black text-6xl bg-gradient-to-b from-white/10 to-white/[0.02] bg-clip-text text-transparent mb-4">{step.n}</p>
                <h3 className="text-xl font-unbounded font-bold text-white mb-2">{step.title}</h3>
                <p className="text-zinc-400">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ SOCIAL PROOF ============================ */}
      <section className="relative z-10 py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">Built by teens, for teens</p>
          <blockquote className="text-2xl md:text-4xl font-unbounded font-bold leading-snug text-white mb-8">
            &ldquo;I understand P/E ratios, compounding and margin of safety before most adults do — because I got to make the mistakes for free first.&rdquo;
          </blockquote>
          <div className="inline-flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-black">N</div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Neil P.</p>
              <p className="text-zinc-500 text-xs">Founder · Leaderboard #1 · Rank: Master</p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================== SEO CONTENT KEPT =========================== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16 text-zinc-300">
        <div className="rounded-3xl bg-black/60 backdrop-blur-2xl border border-white/10 p-8 md:p-12 space-y-6">
          <h2 className="text-3xl md:text-4xl font-unbounded font-bold text-white">What is TeenVest?</h2>
          <p className="text-base md:text-lg leading-relaxed">
            TeenVest is a free stock market simulation game that teaches teens, kids, and beginners how to invest — safely, in real time, and without risking a single dollar. Every new player starts with a <strong>$25,000 virtual portfolio</strong> and can buy or sell more than 1,000 real assets, including U.S. stocks, ETFs, cryptocurrencies (Bitcoin, Ethereum, Solana), commodities (Gold, Silver, Oil), and currency pairs. Prices are pulled live from yfinance and update every minute, so you learn against the actual market — not a fake one.
          </p>
          <h2 className="text-2xl md:text-3xl font-unbounded font-bold text-white pt-4">How the game teaches investing</h2>
          <p className="text-base md:text-lg leading-relaxed">
            Learning happens across <strong>six game modes</strong>. In <em>Learn Mode</em>, you walk through guided lessons on P/E ratios, moats, margin of safety, free cash flow, ROE and CAPE. <em>Chaos Mode</em> throws you into 50 levels of five different market crises. <em>News Reactor</em> trains you to read headlines and react. <em>Oracle Mode</em> rewards long-term thinking, <em>Wolf Mode</em> tests hype resistance and <em>AI Bubble Mode</em> teaches reflective analysis. Every mode you complete earns 200 XP and moves you up the rank ladder: Beginner &rarr; Novice &rarr; Trader &rarr; Investor &rarr; Pro &rarr; Master.
          </p>
          <h2 className="text-2xl md:text-3xl font-unbounded font-bold text-white pt-4">Built-in AI stock analyst</h2>
          <p className="text-base md:text-lg leading-relaxed">
            Once you&apos;re signed in, our <strong>AI Analyst</strong> reviews your portfolio and gives you a personalised risk profile — Conservative, Moderate or Aggressive — plus 3-5 buy ideas from a curated universe with a thesis for each. It&apos;s powered by GPT-5 and it&apos;s free.
          </p>
          <h2 className="text-2xl md:text-3xl font-unbounded font-bold text-white pt-4">Who is it for?</h2>
          <p className="text-base md:text-lg leading-relaxed">
            TeenVest is designed for <strong>teens (13-19), kids, students, parents and educators</strong> who want a safe way to learn financial literacy. It works in any modern browser and installs as a Progressive Web App on iOS, Android, Windows and macOS. No credit card, no real money, no risk — just real markets and real skill-building.
          </p>
        </div>
      </section>

      {/* ============================ FINAL CTA BAND ============================ */}
      <section className="relative z-10 py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-3xl bg-gradient-to-br from-yellow-300 via-yellow-500 to-yellow-700 p-10 md:p-16 overflow-hidden">
            <div className="pointer-events-none absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.6) 0%, transparent 50%)' }} />
            <div className="relative max-w-2xl">
              <h2 className="font-unbounded font-black text-4xl md:text-6xl text-black leading-[1.05] mb-6">
                Start learning<br />the market today.
              </h2>
              <p className="text-black/80 text-lg mb-8">Free forever. No credit card. Real prices. Zero risk.</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openRegister}
                  className="inline-flex items-center gap-2 bg-black text-white font-bold text-base px-7 py-4 rounded-full hover:scale-[1.03] transition-transform"
                  data-testid="cta-get-started-btn"
                >
                  Get Started Free <CaretRight size={16} weight="bold" />
                </button>
                <Link
                  to="/central-message"
                  className="inline-flex items-center gap-2 bg-black/10 border border-black/20 text-black font-semibold text-base px-6 py-4 rounded-full hover:bg-black/20 transition-colors"
                >
                  Read the manifesto
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================ FOOTER ================================ */}
      <footer className="relative z-10 border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-6 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" className="w-6 h-6 opacity-70" />
            <span className="font-unbounded font-bold">TeenVest</span>
            <span className="text-zinc-600">· Empowering the next generation.</span>
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/about" className="hover:text-gold transition-colors">About</Link>
            <Link to="/blog" className="hover:text-gold transition-colors">Blog</Link>
            <Link to="/central-message" className="hover:text-gold transition-colors">Manifesto</Link>
            <a href="mailto:hello@teenvest4growth.com" className="hover:text-gold transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
